import React, { useCallback, useMemo, useState } from 'react';
import { Col, Row } from '@/lib/utils';
import { DemiLightText, RegularText } from '@/lib/Text';
import dayjs from 'dayjs';

import {
    CellState,
    ExceptionExclude,
    ExceptionInclude,
    WeekSchedule,
    BrushType,
} from '../types/workingHours';
import {
    applyBrush,
    computeCellState,
    DayOfMonth,
    getDaysOfMonth,
    updateExcludeAt,
    updateIncludeAt,
} from '../utils/workGridMappers';
import { CoachRowHeader } from './CoachRowHeader';
import { WorkGridCell } from './WorkGridCell';
import { useDragSelection, CellKey } from '../hooks/useDragSelection';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { TimeSelect } from './TimeSelect';

type Coach = { coachId: string; coachName: string };

type Props = {
    coaches: Coach[];
    year: number;
    month: number;
    weekScheduleMap: Record<string, WeekSchedule>;
    exceptionIncludeMap: Record<string, ExceptionInclude[]>;
    exceptionExcludeMap: Record<string, ExceptionExclude[]>;
    onWeekScheduleChange: (coachId: string, next: WeekSchedule) => void;
    onIncludeMapChange: (next: Record<string, ExceptionInclude[]>) => void;
    onExcludeMapChange: (next: Record<string, ExceptionExclude[]>) => void;
    isCoachModified: (coachId: string) => boolean;
    selectedBrush: BrushType | null;
    /** 강사 간 근무 데이터 스왑 */
    onSwapCoaches?: (srcCoachId: string, dstCoachId: string) => void;
};

export const WorkGridTable = ({
    coaches,
    year,
    month,
    weekScheduleMap,
    exceptionIncludeMap,
    exceptionExcludeMap,
    onWeekScheduleChange,
    onIncludeMapChange,
    onExcludeMapChange,
    isCoachModified,
    selectedBrush,
    onSwapCoaches,
}: Props) => {
    const days = useMemo<DayOfMonth[]>(() => getDaysOfMonth(year, month), [year, month]);
    const coachOrder = useMemo(() => coaches.map((c) => c.coachId), [coaches]);
    const dateOrder = useMemo(() => days.map((d) => d.date), [days]);

    // 브러시 적용 후 시간을 채워야 할 include 셀들 (popover 타겟)
    const [pendingIncludeTargets, setPendingIncludeTargets] = useState<CellKey[] | null>(null);
    // 단일 셀 편집용 popover
    const [editingCell, setEditingCell] = useState<CellKey | null>(null);

    const handleCommitSelection = useCallback(
        (cells: CellKey[]) => {
            if (!selectedBrush || cells.length === 0) return;

            const { includeMap, excludeMap } = applyBrush(
                selectedBrush,
                cells,
                exceptionIncludeMap,
                exceptionExcludeMap,
            );
            onIncludeMapChange(includeMap);
            onExcludeMapChange(excludeMap);

            if (selectedBrush.kind === 'include' && (!selectedBrush.startTime || !selectedBrush.endTime)) {
                setPendingIncludeTargets(cells);
            }
        },
        [selectedBrush, exceptionIncludeMap, exceptionExcludeMap, onIncludeMapChange, onExcludeMapChange],
    );

    const { isSelected, handleMouseDown, handleMouseEnter } = useDragSelection({
        coachOrder,
        dateOrder,
        onCommit: handleCommitSelection,
    });

    const handleCellMouseDown = useCallback(
        (e: React.MouseEvent, cell: CellKey) => {
            if (!selectedBrush) return; // 브러시 미선택 시 드래그 비활성
            e.preventDefault();
            handleMouseDown(cell);
        },
        [selectedBrush, handleMouseDown],
    );

    const handleCellMouseEnter = useCallback(
        (_e: React.MouseEvent, cell: CellKey) => {
            if (!selectedBrush) return;
            handleMouseEnter(cell);
        },
        [selectedBrush, handleMouseEnter],
    );

    const handleCellClick = useCallback(
        (_e: React.MouseEvent, cell: CellKey) => {
            if (selectedBrush) return; // 브러시 적용 중에는 click 대신 drag로 처리됨
            setEditingCell(cell);
        },
        [selectedBrush],
    );

    // 스티키 헤더용 요일 라벨 스타일 — 토/일은 읽기 쉬운 파랑/빨강으로 표시
    const headerCellStyle = (isSaturday: boolean, isSunday: boolean) =>
        `h-[40px] min-w-[40px] w-[40px] border-r border-b border-[#E6E8EA] flex items-center justify-center text-[13px] ${
            isSunday ? 'text-[#B54958]' : isSaturday ? 'text-[#3D73DD]' : 'text-[#6B7280]'
        }`;

    return (
        <Col className="w-full">
            <div className="overflow-x-auto border border-solid border-[#E6E8EA] rounded-[12px] bg-white">
                {/* 헤더 행: 요일 */}
                <div className="flex sticky top-0 z-[2] bg-[#F7F8FA]">
                    <div className="h-[40px] w-[220px] min-w-[220px] border-r border-b border-[#E6E8EA] flex items-center px-[12px]">
                        <DemiLightText fontSize={13} color="#6B7280">
                            강사 · 기본 시간
                        </DemiLightText>
                    </div>
                    {days.map((d) => (
                        <div key={`h-dow-${d.date}`} className={headerCellStyle(d.isSaturday, d.isSunday)}>
                            {d.dayOfWeek}
                        </div>
                    ))}
                </div>
                {/* 헤더 행: 날짜 */}
                <div className="flex sticky top-[40px] z-[2] bg-[#F7F8FA]">
                    <div className="h-[32px] w-[220px] min-w-[220px] border-r border-b border-[#E6E8EA] flex items-center justify-start px-[12px]">
                        <DemiLightText fontSize={13} color="#6B7280">
                            {dayjs(`${year}-${String(month).padStart(2, '0')}-01`).format('YYYY년 M월')}
                        </DemiLightText>
                    </div>
                    {days.map((d) => (
                        <div
                            key={`h-day-${d.date}`}
                            className={`h-[32px] min-w-[40px] w-[40px] border-r border-b border-[#E6E8EA] flex items-center justify-center text-[13px] ${
                                d.isSunday ? 'text-[#B54958]' : d.isSaturday ? 'text-[#3D73DD]' : 'text-[#6B7280]'
                            }`}
                        >
                            {d.day}
                        </div>
                    ))}
                </div>

                {/* 본문 */}
                {coaches.map((coach) => {
                    const schedule = weekScheduleMap[coach.coachId];
                    const includes = exceptionIncludeMap[coach.coachId] ?? [];
                    const excludes = exceptionExcludeMap[coach.coachId] ?? [];

                    return (
                        <div key={coach.coachId} className="flex">
                            <CoachRowHeader
                                coachId={coach.coachId}
                                coachName={coach.coachName}
                                modified={isCoachModified(coach.coachId)}
                                weekSchedule={schedule ?? emptySchedule()}
                                onChangeWeekSchedule={(next) => onWeekScheduleChange(coach.coachId, next)}
                                onSwap={onSwapCoaches}
                            />
                            {days.map((d) => {
                                const state: CellState = computeCellState(
                                    d.date,
                                    d.dayOfWeek,
                                    schedule ?? emptySchedule(),
                                    includes,
                                    excludes,
                                );
                                const cell: CellKey = { coachId: coach.coachId, date: d.date };
                                return (
                                    <WorkGridCell
                                        key={`${coach.coachId}-${d.date}`}
                                        coachId={coach.coachId}
                                        date={d.date}
                                        state={state}
                                        selected={isSelected(cell)}
                                        onMouseDown={(e) => handleCellMouseDown(e, cell)}
                                        onMouseEnter={(e) => handleCellMouseEnter(e, cell)}
                                        onClick={(e) => handleCellClick(e, cell)}
                                    />
                                );
                            })}
                        </div>
                    );
                })}
            </div>

            {/* Include 시간 입력 Popover (드래그 종료 후) */}
            {pendingIncludeTargets && (
                <IncludeTimePrompt
                    targetCount={pendingIncludeTargets.length}
                    onConfirm={(start, end, memo) => {
                        let nextMap = exceptionIncludeMap;
                        pendingIncludeTargets.forEach(({ coachId, date }) => {
                            nextMap = updateIncludeAt(
                                coachId,
                                date,
                                { startTime: start, endTime: end, memo },
                                nextMap,
                            );
                        });
                        onIncludeMapChange(nextMap);
                        setPendingIncludeTargets(null);
                    }}
                    onCancel={() => {
                        // 미입력 상태인 항목 제거
                        let nextMap = { ...exceptionIncludeMap };
                        pendingIncludeTargets.forEach(({ coachId, date }) => {
                            const list = nextMap[coachId] ?? [];
                            nextMap[coachId] = list.filter((i) => !(i.date === date && !i.startTime));
                        });
                        onIncludeMapChange(nextMap);
                        setPendingIncludeTargets(null);
                    }}
                />
            )}

            {/* 단일 셀 편집 Popover */}
            {editingCell && (
                <CellEditPrompt
                    cell={editingCell}
                    includes={exceptionIncludeMap[editingCell.coachId] ?? []}
                    excludes={exceptionExcludeMap[editingCell.coachId] ?? []}
                    onUpdateInclude={(patch) => {
                        onIncludeMapChange(
                            updateIncludeAt(editingCell.coachId, editingCell.date, patch, exceptionIncludeMap),
                        );
                    }}
                    onUpdateExclude={(patch) => {
                        onExcludeMapChange(
                            updateExcludeAt(editingCell.coachId, editingCell.date, patch, exceptionExcludeMap),
                        );
                    }}
                    onClear={() => {
                        const { includeMap, excludeMap } = applyBrush(
                            { kind: 'default' },
                            [editingCell],
                            exceptionIncludeMap,
                            exceptionExcludeMap,
                        );
                        onIncludeMapChange(includeMap);
                        onExcludeMapChange(excludeMap);
                        setEditingCell(null);
                    }}
                    onClose={() => setEditingCell(null)}
                />
            )}
        </Col>
    );
};

const emptySchedule = (): WeekSchedule => ({
    월: { enabled: false, timeSlots: [] },
    화: { enabled: false, timeSlots: [] },
    수: { enabled: false, timeSlots: [] },
    목: { enabled: false, timeSlots: [] },
    금: { enabled: false, timeSlots: [] },
    토: { enabled: false, timeSlots: [] },
    일: { enabled: false, timeSlots: [] },
});

// ----- sub-popovers -----

const IncludeTimePrompt = ({
    targetCount,
    onConfirm,
    onCancel,
}: {
    targetCount: number;
    onConfirm: (startTime: string, endTime: string, memo?: string) => void;
    onCancel: () => void;
}) => {
    const [start, setStart] = useState('');
    const [end, setEnd] = useState('');
    const [memo, setMemo] = useState('');

    return (
        <div
            className="fixed inset-0 z-[99998] bg-black/30 flex items-center justify-center"
            onClick={(e) => {
                if (e.currentTarget === e.target) onCancel();
            }}
        >
            <div
                className="bg-white rounded-[20px] shadow-lg p-[24px] w-[440px]"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="mb-[6px]">
                    <RegularText fontSize={18} color="#1A1A1A">
                        추가 근무 시간 입력
                    </RegularText>
                </div>
                <div className="mb-[16px]">
                    <RegularText fontSize={13} color="#6B7280">
                        선택한 {targetCount}개 셀에 동일하게 적용됩니다
                    </RegularText>
                </div>
                <Row className="items-center mb-[12px]">
                    <TimeSelect value={start} onChange={setStart} disabled={false} placeholder="시작 시간" compact />
                    <RegularText fontSize={16} color="#6B7280" className="mx-[10px]">
                        ~
                    </RegularText>
                    <TimeSelect value={end} onChange={setEnd} disabled={false} placeholder="종료 시간" compact allowDayEnd />
                </Row>
                <input
                    className="w-full h-[42px] px-[14px] rounded-[10px] border border-solid border-[#E6E8EA] text-[14px] outline-none"
                    placeholder="메모 (선택)"
                    value={memo}
                    onChange={(e) => setMemo(e.target.value)}
                />
                <Row className="justify-end gap-[8px] mt-[20px]">
                    <Button
                        variant="ghost"
                        className="h-[40px] w-[96px] rounded-lg bg-[#F4F5F7] hover:bg-[#E8EAED]"
                        onClick={onCancel}
                    >
                        <RegularText fontSize={14}>취소</RegularText>
                    </Button>
                    <Button
                        className={`h-[40px] w-[96px] rounded-lg ${
                            start && end ? 'bg-[#3D73DD] hover:bg-[#3D73DD]/90' : 'bg-[#3D73DD] opacity-40 cursor-not-allowed'
                        } text-white`}
                        onClick={start && end ? () => onConfirm(start, end, memo) : undefined}
                    >
                        <RegularText fontSize={14} color="#FFFFFF">
                            적용
                        </RegularText>
                    </Button>
                </Row>
            </div>
        </div>
    );
};

const CellEditPrompt = ({
    cell,
    includes,
    excludes,
    onUpdateInclude,
    onUpdateExclude,
    onClear,
    onClose,
}: {
    cell: CellKey;
    includes: ExceptionInclude[];
    excludes: ExceptionExclude[];
    onUpdateInclude: (patch: Partial<Pick<ExceptionInclude, 'startTime' | 'endTime' | 'memo'>>) => void;
    onUpdateExclude: (patch: Partial<Pick<ExceptionExclude, 'memo'>>) => void;
    onClear: () => void;
    onClose: () => void;
}) => {
    const inc = includes.find((i) => i.date === cell.date);
    const exc = excludes.find((e) => e.date === cell.date);

    const [start, setStart] = useState(inc?.startTime ?? '');
    const [end, setEnd] = useState(inc?.endTime ?? '');
    const [memo, setMemo] = useState(inc?.memo ?? exc?.memo ?? '');

    const existing: 'include' | 'exclude' | 'none' = inc ? 'include' : exc ? 'exclude' : 'none';

    const handleConfirm = () => {
        if (existing === 'include') {
            onUpdateInclude({ startTime: start, endTime: end, memo });
        } else if (existing === 'exclude') {
            onUpdateExclude({ memo });
        }
        onClose();
    };

    return (
        <div
            className="fixed inset-0 z-[99998] bg-black/30 flex items-center justify-center"
            onClick={(e) => {
                if (e.currentTarget === e.target) onClose();
            }}
        >
            <div
                className="bg-white rounded-[20px] shadow-lg p-[24px] w-[440px]"
                onClick={(e) => e.stopPropagation()}
            >
                <RegularText fontSize={18} color="#1A1A1A" className="mb-[16px]">
                    {dayjs(cell.date).format('YYYY.MM.DD (dd)')} ·{' '}
                    {existing === 'include' ? '추가 근무 (IN)' : existing === 'exclude' ? '근무 제외 (OUT)' : '기본 근무'}
                </RegularText>

                {existing === 'include' && (
                    <>
                        <Row className="items-center mb-[12px]">
                            <TimeSelect
                                value={start}
                                onChange={setStart}
                                disabled={false}
                                placeholder="시작"
                                compact
                            />
                            <RegularText fontSize={16} color="#6B7280" className="mx-[10px]">
                                ~
                            </RegularText>
                            <TimeSelect value={end} onChange={setEnd} disabled={false} placeholder="종료" compact allowDayEnd />
                        </Row>
                    </>
                )}

                {existing !== 'none' && (
                    <input
                        className="w-full h-[42px] px-[14px] rounded-[10px] border border-solid border-[#E6E8EA] text-[14px] outline-none"
                        placeholder="메모 (선택)"
                        value={memo}
                        onChange={(e) => setMemo(e.target.value)}
                    />
                )}

                {existing === 'none' && (
                    <RegularText fontSize={14} color="#6B7280">
                        이 날짜는 기본 근무 상태입니다. 상단 브러시를 선택한 뒤 셀을 클릭하거나 드래그하여 변경하세요.
                    </RegularText>
                )}

                <Row className="justify-between mt-[20px]">
                    {existing !== 'none' ? (
                        <Button
                            variant="ghost"
                            className="h-[40px] px-[16px] rounded-lg bg-[#D6E4F7] text-[#2A4A80] hover:bg-[#C3D6F2]"
                            onClick={onClear}
                        >
                            <RegularText fontSize={14} color="#2A4A80">
                                기본으로 되돌리기
                            </RegularText>
                        </Button>
                    ) : (
                        <div />
                    )}
                    <Row className="gap-[8px]">
                        <Button
                            variant="ghost"
                            className="h-[40px] w-[96px] rounded-lg bg-[#F4F5F7] hover:bg-[#E8EAED]"
                            onClick={onClose}
                        >
                            <RegularText fontSize={14}>닫기</RegularText>
                        </Button>
                        {existing !== 'none' && (
                            <Button
                                className="h-[40px] w-[96px] rounded-lg bg-[#3D73DD] hover:bg-[#3D73DD]/90 text-white"
                                onClick={handleConfirm}
                            >
                                <RegularText fontSize={14} color="#FFFFFF">
                                    저장
                                </RegularText>
                            </Button>
                        )}
                    </Row>
                </Row>
            </div>
        </div>
    );
};
